import * as React from 'react';

type Direction = 'up' | 'down' | 'left' | 'right';

interface Cell {
  row: number;
  col: number;
}

interface GridFocusState {
  row: number;
  col: number;
}

interface UseGridFocusProps {
  maxColumns: number;
  rowCount: number;
  onAddRow: () => boolean;
  onRemoveLastEmptyRow: () => number | null;
  onTypingWhenNotEditing?: (
    key: string,
    enterEditingState: (editRow?: number, editCol?: number) => void,
  ) => void;
  skipCell?: (cell: Cell) => boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  fieldRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  editOnFocus?: boolean;
}

const findNextValidCell = (
  current: Cell,
  direction: Direction,
  maxColumns: number,
  rowCount: number,
  skipCell?: (cell: Cell) => boolean,
): Cell | null => {
  const { row, col } = current;

  const isValidCell = (r: number, c: number): boolean => {
    if (r < 0 || r >= rowCount || c < 0 || c >= maxColumns) return false;
    return !skipCell || !skipCell({ row: r, col: c });
  };

  switch (direction) {
    case 'up':
      for (let r = row - 1; r >= 0; r--) {
        if (isValidCell(r, col)) return { row: r, col };
      }
      break;
    case 'down':
      for (let r = row + 1; r < rowCount; r++) {
        if (isValidCell(r, col)) return { row: r, col };
      }
      break;
    case 'left':
      for (let c = col - 1; c >= 0; c--) {
        if (isValidCell(row, c)) return { row, col: c };
      }
      break;
    case 'right':
      for (let c = col + 1; c < maxColumns; c++) {
        if (isValidCell(row, c)) return { row, col: c };
      }
      break;
  }
  return null;
};

const findFirstFocusableWithin = (element: HTMLElement | null) => {
  if (!element) return null;
  const candidates = element.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  );
  for (let i = 0; i < candidates.length; i++) {
    if (!candidates[i].closest('template')) {
      return candidates[i] as HTMLElement;
    }
  }
  return null;
};

const getScrollableParent = (
  element: HTMLElement | null,
): HTMLElement | null => {
  if (!element) return null;
  let current = element.parentElement;
  while (current) {
    const { overflow, overflowY } = getComputedStyle(current);
    if (/(auto|scroll)/.test(overflow + overflowY)) return current;
    current = current.parentElement;
  }
  return null;
};

export const useGridFocus = ({
  maxColumns,
  rowCount,
  onAddRow,
  onRemoveLastEmptyRow,
  onTypingWhenNotEditing,
  skipCell,
  containerRef,
  fieldRefs,
  editOnFocus: _editOnFocus = false,
}: UseGridFocusProps) => {
  const [focusState, setFocusState] = React.useState<GridFocusState>({
    row: 0,
    col: 0,
  });
  const [editingState, setEditingState] = React.useState<GridFocusState | null>(
    null,
  );

  const { row, col } = focusState;

  const shouldClickRef = React.useRef(false);
  const isNavigatingRef = React.useRef(false);

  React.useEffect(() => {
    if (!editingState) return;
    const cellEl =
      fieldRefs.current[editingState.row * maxColumns + editingState.col];
    if (!cellEl) return;
    const focusable = findFirstFocusableWithin(cellEl);
    if (!focusable) return;
    const activeEl = document.activeElement;
    const isChildFocused = cellEl.contains(activeEl) && activeEl !== cellEl;
    if (focusable !== activeEl && !isChildFocused) {
      focusable.focus();
    }
    if (shouldClickRef.current && !(focusable instanceof HTMLInputElement)) {
      focusable.click();
    }
    shouldClickRef.current = false;
  }, [editingState, fieldRefs, maxColumns]);

  const enterEditingState = React.useCallback(
    (editRow: number = row, editCol: number = col) => {
      const currFocusable = fieldRefs.current[editRow * maxColumns + editCol];
      const firstNestedFocusable = findFirstFocusableWithin(currFocusable);
      if (firstNestedFocusable !== document.activeElement) {
        firstNestedFocusable?.focus();
      }
      setEditingState({ row: editRow, col: editCol });
    },
    [fieldRefs, maxColumns, row, col],
  );

  const moveToCell = React.useCallback((nextCell: Cell | null) => {
    if (!nextCell) return;
    isNavigatingRef.current = true;
    setFocusState(nextCell);
    setEditingState(nextCell);
  }, []);

  const findValidCellInPreviousRow = React.useCallback(() => {
    for (let r = row - 1; r >= 0; r--) {
      for (let c = maxColumns - 1; c >= 0; c--) {
        if (!skipCell || !skipCell({ row: r, col: c })) {
          return { row: r, col: c };
        }
      }
    }
    return null;
  }, [maxColumns, row, skipCell]);

  const findValidCellInNextRow = React.useCallback(() => {
    const maxRow =
      row < rowCount - 1 ? rowCount : onAddRow() ? rowCount + 1 : rowCount;
    for (let r = row + 1; r < maxRow; r++) {
      for (let c = 0; c < maxColumns; c++) {
        if (!skipCell || !skipCell({ row: r, col: c })) {
          return { row: r, col: c };
        }
      }
    }
    return null;
  }, [maxColumns, onAddRow, row, rowCount, skipCell]);

  const handleContainerKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (document.activeElement !== containerRef.current) return;

      if (['ArrowLeft', 'ArrowRight'].includes(e.key) && !!editingState) {
        return;
      }

      if (e.key === 'Enter' && editingState === null) {
        e.preventDefault();
        shouldClickRef.current = true;
        enterEditingState();
        return;
      }

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          containerRef.current?.focus();
          break;
        case 'ArrowUp': {
          e.preventDefault();
          const newLastIndex = onRemoveLastEmptyRow();

          let nextCell = null;
          if (newLastIndex !== null && row === rowCount - 1) {
            nextCell = findNextValidCell(
              { row: newLastIndex + 1, col },
              'up',
              maxColumns,
              rowCount,
              skipCell,
            );
          } else {
            nextCell = findNextValidCell(
              focusState,
              'up',
              maxColumns,
              rowCount,
              skipCell,
            );
          }
          moveToCell(nextCell);
          break;
        }
        case 'ArrowDown':
          e.preventDefault();
          if (focusState.row < rowCount - 1 || onAddRow()) {
            const nextCell = findNextValidCell(
              focusState,
              'down',
              maxColumns,
              rowCount + (focusState.row === rowCount - 1 ? 1 : 0),
              skipCell,
            );
            moveToCell(nextCell);
          }
          break;
        case 'ArrowLeft': {
          e.preventDefault();
          const leftCell = findNextValidCell(
            focusState,
            'left',
            maxColumns,
            rowCount,
            skipCell,
          );
          moveToCell(leftCell);
          break;
        }
        case 'ArrowRight': {
          e.preventDefault();
          const rightCell = findNextValidCell(
            focusState,
            'right',
            maxColumns,
            rowCount,
            skipCell,
          );
          moveToCell(rightCell);
          break;
        }
        case 'Tab':
          e.preventDefault();
          e.stopPropagation();
          if (e.shiftKey) {
            let nextCell = findNextValidCell(
              focusState,
              'left',
              maxColumns,
              rowCount,
              skipCell,
            );
            if (!nextCell && row > 0) {
              onRemoveLastEmptyRow();
              nextCell = findValidCellInPreviousRow();
            }
            moveToCell(nextCell);
          } else {
            let nextCell = findNextValidCell(
              focusState,
              'right',
              maxColumns,
              rowCount,
              skipCell,
            );
            if (!nextCell) {
              nextCell = findValidCellInNextRow();
            }
            moveToCell(nextCell);
          }
          break;
        default:
          if (
            !editingState &&
            e.key.length === 1 &&
            !e.ctrlKey &&
            !e.altKey &&
            !e.metaKey
          ) {
            e.preventDefault();
            if (onTypingWhenNotEditing)
              onTypingWhenNotEditing(e.key, enterEditingState);
          }
          break;
      }
    },
    [
      containerRef,
      editingState,
      enterEditingState,
      focusState,
      rowCount,
      onAddRow,
      onRemoveLastEmptyRow,
      row,
      moveToCell,
      col,
      maxColumns,
      skipCell,
      findValidCellInPreviousRow,
      findValidCellInNextRow,
      onTypingWhenNotEditing,
    ],
  );

  const handleFieldKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (!(e.currentTarget as HTMLElement).contains(e.target as Node)) return;
      if (['ArrowLeft', 'ArrowRight'].includes(e.key) && !!editingState) {
        return;
      }
      if (
        [
          'Escape',
          'Enter',
          'Tab',
          'ArrowDown',
          'ArrowUp',
          'ArrowLeft',
          'ArrowRight',
        ].includes(e.key)
      ) {
        e.preventDefault();
        const scrollableParent = getScrollableParent(
          containerRef.current as HTMLElement,
        );
        const scrollTopPosition = scrollableParent?.scrollTop;
        (document.activeElement as HTMLElement)?.blur();
        containerRef.current?.focus();
        if (
          e.key === 'Enter' &&
          scrollableParent &&
          scrollTopPosition !== undefined
        ) {
          scrollableParent.scrollTop = scrollTopPosition;
        }
        setEditingState(null);

        let direction: Direction | null = null;
        if (e.key === 'Tab' && e.shiftKey) {
          direction = 'left';
        } else if (e.key === 'Enter' || e.key === 'Tab') {
          direction = 'right';
        }
        if (direction) {
          let nextCell = findNextValidCell(
            focusState,
            direction,
            maxColumns,
            rowCount,
            skipCell,
          );
          if (!nextCell && e.key === 'Tab') {
            if (e.shiftKey) {
              onRemoveLastEmptyRow();
              nextCell = findValidCellInPreviousRow();
            } else {
              nextCell = findValidCellInNextRow();
            }
          }
          moveToCell(nextCell);
        }
      }
    },
    [
      editingState,
      containerRef,
      focusState,
      maxColumns,
      rowCount,
      skipCell,
      moveToCell,
      onRemoveLastEmptyRow,
      findValidCellInPreviousRow,
      findValidCellInNextRow,
    ],
  );

  const getCommonProps = React.useCallback(
    (rowIndex: number, colIndex: number) => ({
      className: `outline-none ${
        focusState.row === rowIndex && focusState.col === colIndex
          ? 'ring-2 ring-inset ring-primary'
          : ''
      }`,
      ref: (el: HTMLDivElement | null) => {
        fieldRefs.current[rowIndex * maxColumns + colIndex] = el;
      },
      onFocus: () => {
        if (isNavigatingRef.current) {
          isNavigatingRef.current = false;
          return;
        }
        shouldClickRef.current = true;
        setFocusState({ row: rowIndex, col: colIndex });
        setEditingState({ row: rowIndex, col: colIndex });
      },
      onBlur: (e: React.FocusEvent) => {
        if (!e.relatedTarget) return;
        const cell = e.currentTarget;
        if (cell.contains(e.relatedTarget as Node)) return;
        setEditingState(null);
      },
      onKeyDown: handleFieldKeyDown,
    }),
    [focusState, maxColumns, fieldRefs, handleFieldKeyDown],
  );

  const detectDirection = (
    current: Cell,
    target: Cell,
  ): Direction | undefined => {
    if (target.row < current.row) return 'up';
    if (target.row > current.row) return 'down';
    if (target.col < current.col) return 'left';
    if (target.col > current.col) return 'right';
    return undefined;
  };

  const safeFocusState = React.useCallback(
    (newState: GridFocusState | ((prev: GridFocusState) => GridFocusState)) => {
      const targetState =
        typeof newState === 'function' ? newState(focusState) : newState;

      if (!skipCell || !skipCell(targetState)) {
        setFocusState(targetState);
        return;
      }

      const direction = detectDirection(focusState, targetState);
      if (!direction) return;

      let nextCell = findNextValidCell(
        focusState,
        direction,
        maxColumns,
        rowCount,
        skipCell,
      );

      if (!nextCell && (direction === 'up' || direction === 'down')) {
        const horizDirection = detectDirection(focusState, {
          ...targetState,
          row: focusState.row,
        });
        if (horizDirection) {
          nextCell = findNextValidCell(
            focusState,
            horizDirection,
            maxColumns,
            rowCount,
            skipCell,
          );
        }
      }

      if (nextCell) {
        setFocusState(nextCell);
      }
    },
    [focusState, skipCell, maxColumns, rowCount],
  );

  const exitEditing = React.useCallback(() => {
    (document.activeElement as HTMLElement)?.blur();
    containerRef.current?.focus();
    setEditingState(null);
  }, [containerRef]);

  const exitEditingAndNavigate = React.useCallback(
    (direction: Direction = 'right') => {
      (document.activeElement as HTMLElement)?.blur();
      containerRef.current?.focus();
      setEditingState(null);
      let nextCell = findNextValidCell(
        focusState,
        direction,
        maxColumns,
        rowCount,
        skipCell,
      );
      if (!nextCell) {
        nextCell =
          direction === 'left'
            ? findValidCellInPreviousRow()
            : findValidCellInNextRow();
      }
      moveToCell(nextCell);
    },
    [
      containerRef,
      focusState,
      maxColumns,
      rowCount,
      skipCell,
      moveToCell,
      findValidCellInPreviousRow,
      findValidCellInNextRow,
    ],
  );

  const focusCell = React.useCallback(
    (targetRow: number, targetCol: number) => {
      (document.activeElement as HTMLElement)?.blur();
      containerRef.current?.focus();
      setEditingState(null);
      moveToCell({ row: targetRow, col: targetCol });
    },
    [containerRef, moveToCell],
  );

  return {
    focusState,
    editingState,
    setFocusState: safeFocusState,
    setEditingState,
    handleContainerKeyDown,
    getCommonProps,
    exitEditing,
    exitEditingAndNavigate,
    focusCell,
  };
};
